/******* Type definitions for v8.15.6 *******/

import { ObsidianPropertyType } from 'src/components/obsidian-dataloom/shared/frontmatter/types';

/**
 * v8.15.6
 */
export interface LoomState {
  pluginVersion: string;
  model: TableModel;
}

export interface TableModel {
  columns: Column[];
  rows: Row[];
  filters: Filter[];
  settings: TableSettings;
  sources: Source[];
  externalRowOrder: ExternalRowOrder[];
}

export interface ExternalRowOrder {
  sourceId: string;
  index: number;
  uniqueId: string; //This could be a file path, tag name, or url
}

export interface TableSettings {
  numFrozenColumns: number;
  showCalculationRow: boolean;
}

export interface Column {
  id: string;
  sortDir: SortDir;
  width: string;
  type: CellType;
  isVisible: boolean;
  dateFormat: DateFormat;
  dateFormatSeparator: DateFormatSeparator;
  hour12: boolean;
  includeTime: boolean;
  content: string;
  numberFormat: NumberFormat;
  currencyType: CurrencyType;
  numberPrefix: string;
  numberSuffix: string;
  numberSeparator: string;
  shouldWrapOverflow: boolean;
  tags: Tag[];
  calculationType: CalculationType;
  aspectRatio: AspectRatio;
  horizontalPadding: PaddingSize;
  verticalPadding: PaddingSize;
  frontmatterKey: string | null;
}

export interface Row {
  id: string;
  index: number;
  creationDateTime: string;
  lastEditedDateTime: string;
  sourceId: string | null;
  cells: Cell[];
}

export interface BaseCell {
  id: string;
  columnId: string;
  hasValidFrontmatter: boolean | null;
}

export interface TextCell extends BaseCell {
  content: string;
}

export interface EmbedCell extends BaseCell {
  isExternal: boolean;
  pathOrUrl: string;
  alias: string | null;
}

export interface EmbedUrl {
  type: 'url';
  url: string;
}

export interface EmbedPath {
  type: 'path';
  path: string;
  alias: string | null;
}

export interface FileCell extends BaseCell {
  path: string;
  alias: string | null; //We save an alias to be able to serialize into the frontmatter
}

export interface CheckboxCell extends BaseCell {
  value: boolean;
}

export interface TagCell extends BaseCell {
  tagId: string | null;
}

export interface MultiTagCell extends BaseCell {
  tagIds: string[];
}

export interface NumberCell extends BaseCell {
  value: number | null;
}

export interface DateCell extends BaseCell {
  dateTime: string | null;
}

export type CreationTimeCell = BaseCell;

export type LastEditedTimeCell = BaseCell;

export type SourceCell = BaseCell;

export interface SourceFileCell extends BaseCell {
  path: string;
}

export type Cell =
  | TextCell
  | EmbedCell
  | FileCell
  | CheckboxCell
  | TagCell
  | MultiTagCell
  | NumberCell
  | DateCell
  | CreationTimeCell
  | LastEditedTimeCell
  | SourceCell
  | SourceFileCell;

export interface Tag {
  id: string;
  content: string;
  color: Color;
}

export enum Color {
  ANTD_MAGENTA = 'antd_magenta',
  ANTD_RED = 'antd_red',
  ANTD_VOLCANO = 'antd_volcano',
  ANTD_ORANGE = 'antd_orange',
  ANTD_GOLD = 'antd_gold',
  ANTD_LIME = 'antd_lime',
  ANTD_GREEN = 'antd_green',
  ANTD_CYAN = 'antd_cyan',
  ANTD_BLUE = 'antd_blue',
  ANTD_GEEKBLUE = 'antd_geekblue',
  ANTD_PURPLE = 'antd_purple',
  LIGHT_GRAY = 'light gray',
  GRAY = 'gray',
  BROWN = 'brown',
  ORANGE = 'orange',
  YELLOW = 'yellow',
  GREEN = 'green',
  BLUE = 'blue',
  PURPLE = 'purple',
  PINK = 'pink',
  RED = 'red',
}

export enum PaddingSize {
  UNSET = 'unset',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  XXL = '2xl',
  XXXL = '3xl',
  XXXXL = '4xl',
}

export enum SortDir {
  ASC = 'asc',
  DESC = 'desc',
  NONE = 'default',
}

export enum CellType {
  SOURCE_FILE = 'source-file',
  SOURCE = 'source',
  TEXT = 'text',
  EMBED = 'embed',
  FILE = 'file',
  NUMBER = 'number',
  TAG = 'tag',
  MULTI_TAG = 'multi-tag',
  DATE = 'date',
  CHECKBOX = 'checkbox',
  CREATION_TIME = 'creation-time',
  LAST_EDITED_TIME = 'last-edited-time',
}

export enum DateFormat {
  MM_DD_YYYY = 'mmddyyyy',
  DD_MM_YYYY = 'ddmmyyyy',
  YYYY_MM_DD = 'yyyymmdd',
}

export enum DateFormatSeparator {
  HYPHEN = '-',
  SLASH = '/',
  DOT = '.',
}

export enum NumberFormat {
  NUMBER = 'number',
  CURRENCY = 'currency',
}

export enum CurrencyType {
  ARGENTINA = 'ARS',
  AUSTRALIA = 'AUD',
  CANADA = 'CAD',
  COLOMBIA = 'COP',
  DENMARK = 'DKK',
  UAE = 'AED',
  EUROPE = 'EUR',
  ICELAND = 'ISK',
  ISRAEL = 'ILS',
  MEXICO = 'MXN',
  NORWAY = 'NOK',
  GREAT_BRITAIN = 'GBP',
  BRAZIL = 'BRL',
  SAUDI_ARABIA = 'SAR',
  RUSSIA = 'RUB',
  INDIA = 'INR',
  SINGAPORE = 'SGB',
  SWEDEN = 'SEK',
  SWITZERLAND = 'CHF',
  UNITED_STATES = 'USD',
  SOUTH_KOREA = 'KRW',
  JAPAN = 'JPY',
  CHINA = 'CNY',
}

export enum AspectRatio {
  UNSET = 'unset',
  NINE_BY_SIXTEEN = '9/16',
  FOUR_BY_THREE = '4/3',
  SIXTEEN_BY_NINE = '16/9',
}

/********** CALCULATIONS **********/
export enum GeneralCalculation {
  NONE = 'none',
  COUNT_ALL = 'count-all',
  COUNT_VALUES = 'count-values',
  COUNT_UNIQUE = 'count-unique',
  COUNT_EMPTY = 'count-empty',
  COUNT_NOT_EMPTY = 'count-not-empty',
  PERCENT_EMPTY = 'percent-empty',
  PERCENT_NOT_EMPTY = 'percent-not-empty',
}

export enum NumberCalculation {
  SUM = 'sum',
  AVG = 'avg',
  MIN = 'min',
  MAX = 'max',
  MEDIAN = 'median',
  RANGE = 'range',
}

export type CalculationType = GeneralCalculation | NumberCalculation;

/************* FILTERS ****************/
export type FilterOperator = 'and' | 'or';

export enum TextFilterCondition {
  IS = 'is',
  IS_NOT = 'is-not',
  CONTAINS = 'contains',
  DOES_NOT_CONTAIN = 'does-not-contain',
  STARTS_WITH = 'starts-with',
  ENDS_WITH = 'ends-with',
  IS_EMPTY = 'is-empty',
  IS_NOT_EMPTY = 'is-not-empty',
}

export enum NumberFilterCondition {
  IS_EQUAL = 'is-equal',
  IS_NOT_EQUAL = 'is-not-equal',
  IS_GREATER = 'is-greater',
  IS_LESS = 'is-less',
  IS_GREATER_OR_EQUAL = 'is-greater-or-equal',
  IS_LESS_OR_EQUAL = 'is-less-or-equal',
  IS_EMPTY = 'is-empty',
  IS_NOT_EMPTY = 'is-not-empty',
}

//TODO add support for more date types
export enum DateFilterCondition {
  IS = 'is',
  IS_BEFORE = 'is-before',
  IS_AFTER = 'is-after',
  IS_EMPTY = 'is-empty',
  IS_NOT_EMPTY = 'is-not-empty',
}

export enum DateFilterOption {
  UNSELECTED = 'unselected',
  TODAY = 'today',
  TOMORROW = 'tomorrow',
  YESTERDAY = 'yesterday',
  ONE_WEEK_AGO = 'one-week-ago',
  ONE_WEEK_FROM_NOW = 'one-week-from-now',
  ONE_MONTH_AGO = 'one-month-ago',
  ONE_MONTH_FROM_NOW = 'one-month-from-now',
}

export type FilterCondition =
  | TextFilterCondition
  | DateFilterCondition
  | NumberFilterCondition;

export interface BaseFilter {
  id: string;
  columnId: string;
  operator: FilterOperator;
  isEnabled: boolean;
}

/* Text filter */
export type TextCondition = TextFilterCondition;

export interface TextFilter extends BaseFilter {
  type: CellType.TEXT;
  condition: TextCondition;
  text: string;
}

/* File filter */
export type FileCondition = TextFilterCondition;

export interface FileFilter extends BaseFilter {
  type: CellType.FILE;
  condition: FileCondition;
  text: string;
}

/* Checkbox filter */
export type CheckboxCondition =
  | TextFilterCondition.IS
  | TextFilterCondition.IS_NOT;

export interface CheckboxFilter extends BaseFilter {
  type: CellType.CHECKBOX;
  condition: CheckboxCondition;
  value: boolean;
}

/* Tag filter */
export type TagCondition =
  | TextFilterCondition.IS
  | TextFilterCondition.IS_NOT
  | TextFilterCondition.IS_EMPTY
  | TextFilterCondition.IS_NOT_EMPTY;

export interface TagFilter extends BaseFilter {
  type: CellType.TAG;
  condition: TagCondition;
  tagId: string;
}

/* Multi-tag filter */
export type MultiTagCondition =
  | TextFilterCondition.CONTAINS
  | TextFilterCondition.DOES_NOT_CONTAIN
  | TextFilterCondition.IS_EMPTY
  | TextFilterCondition.IS_NOT_EMPTY;

export interface MultiTagFilter extends BaseFilter {
  type: CellType.MULTI_TAG;
  condition: MultiTagCondition;
  tagIds: string[];
}

/* Embed filter */
export type EmbedCondition =
  | TextFilterCondition.IS_EMPTY
  | TextFilterCondition.IS_NOT_EMPTY;

export interface EmbedFilter extends BaseFilter {
  type: CellType.EMBED;
  condition: EmbedCondition;
  text: string;
}

/* Number filter */
export type NumberCondition = NumberFilterCondition;

export interface NumberFilter extends BaseFilter {
  type: CellType.NUMBER;
  condition: NumberCondition;
  text: string;
}

/* Date filter */
export type DateCondition = DateFilterCondition;

export interface DateFilter extends BaseFilter {
  type: CellType.DATE;
  condition: DateCondition;
  option: DateFilterOption;
  dateTime: string | null;
}

/* Creation time filter  */
export type CreationTimeCondition =
  | DateFilterCondition.IS
  | DateFilterCondition.IS_AFTER
  | DateFilterCondition.IS_BEFORE;

export interface CreationTimeFilter extends BaseFilter {
  type: CellType.CREATION_TIME;
  condition: CreationTimeCondition;
  option: DateFilterOption;
  dateTime: string | null;
}

/* Last edited filter  */
export type LastEditedTimeCondition =
  | DateFilterCondition.IS
  | DateFilterCondition.IS_AFTER
  | DateFilterCondition.IS_BEFORE;

export interface LastEditedTimeFilter extends BaseFilter {
  type: CellType.LAST_EDITED_TIME;
  condition: LastEditedTimeCondition;
  option: DateFilterOption;
  dateTime: string | null;
}

/* Source File condition */
export type SourceFileCondition = TextFilterCondition;

export interface SourceFileFilter extends BaseFilter {
  type: CellType.SOURCE_FILE;
  condition: SourceFileCondition;
  text: string;
}

export type Filter =
  | TextFilter
  | TagFilter
  | MultiTagFilter
  | CheckboxFilter
  | FileFilter
  | EmbedFilter
  | NumberFilter
  | DateFilter
  | CreationTimeFilter
  | LastEditedTimeFilter
  | SourceFileFilter;

interface BaseSource {
  id: string;
  type: SourceType;
}

export interface ObsidianFolderSource extends BaseSource {
  type: SourceType.FOLDER;
  path: string;
  includeSubfolders: boolean;
}

export interface ObsidianFrontmatterSource extends BaseSource {
  type: SourceType.FRONTMATTER;
  propertyType: ObsidianPropertyType;
  propertyKey: string;
  filterCondition:
    | TextFilterCondition
    | NumberFilterCondition
    | DateFilterCondition
    | null;
  filterText: string;
}

export type Source = ObsidianFolderSource | ObsidianFrontmatterSource;

export enum SourceType {
  FOLDER = 'folder',
  FRONTMATTER = 'frontmatter',
}
